import { useState, useEffect } from 'react'
import type { Decisao } from '../types'
import { api } from '../api'

const STATUS_LABELS: Record<string, { label: string; cor: string }> = {
  proposta: { label: 'Proposta', cor: 'bg-yellow-100 text-yellow-800' },
  aceita: { label: 'Aceita', cor: 'bg-green-100 text-green-800' },
  rejeitada: { label: 'Rejeitada', cor: 'bg-red-100 text-red-800' },
  substituida: { label: 'Substituida', cor: 'bg-gray-100 text-gray-800' },
  adiada: { label: 'Adiada', cor: 'bg-blue-100 text-blue-800' },
}

export default function DecisoesPage() {
  const [decisoes, setDecisoes] = useState<Decisao[]>([])
  const [selected, setSelected] = useState<Decisao | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [loading, setLoading] = useState(true)

  const load = () => api.getDecisoes().then((d) => { setDecisoes(d); setLoading(false) })
  useEffect(() => { load() }, [])

  if (loading) return <div className="flex items-center justify-center h-full text-gray-400">Carregando...</div>

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Registro de Decisoes</h1>
          <p className="text-sm text-gray-500">{decisoes.length} decisao(oes) registrada(s)</p>
        </div>
        <button onClick={() => { setSelected(null); setShowForm(true) }}
          className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
          + Nova Decisao
        </button>
      </div>

      {/* Lista */}
      <div className="space-y-4">
        {decisoes.map((d) => (
          <div key={d.id} onClick={() => setSelected(d)}
            className="bg-white rounded-xl shadow-sm border p-5 cursor-pointer hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs font-mono text-gray-400">ADR-{String(d.numero).padStart(3, '0')}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_LABELS[d.status]?.cor}`}>
                {STATUS_LABELS[d.status]?.label}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-800">{d.titulo}</h3>
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{d.contexto}</p>
            {d.opcoes.length > 0 && (
              <p className="text-xs text-gray-400 mt-2">{d.opcoes.length} opcao(oes) avaliada(s)</p>
            )}
          </div>
        ))}
        {decisoes.length === 0 && (
          <p className="text-center text-gray-400 py-12">Nenhuma decisao registrada ainda.</p>
        )}
      </div>

      {/* Detail modal */}
      {selected && !showForm && (
        <DecisaoDetail
          decisao={selected}
          onClose={() => setSelected(null)}
          onEdit={() => setShowForm(true)}
          onStatusChange={async (status) => {
            await api.updateDecisao(selected.id, { status })
            load()
            setSelected(null)
          }}
        />
      )}

      {/* Form modal */}
      {showForm && (
        <DecisaoForm
          decisao={selected}
          onClose={() => { setShowForm(false); setSelected(null) }}
          onSaved={() => { setShowForm(false); setSelected(null); load() }}
        />
      )}
    </div>
  )
}

function DecisaoDetail({ decisao: d, onClose, onEdit, onStatusChange }: {
  decisao: Decisao
  onClose: () => void
  onEdit: () => void
  onStatusChange: (status: string) => void
}) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-12 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 mb-12" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono text-gray-400">ADR-{String(d.numero).padStart(3, '0')}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_LABELS[d.status]?.cor}`}>
              {STATUS_LABELS[d.status]?.label}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        <div className="p-6 space-y-5">
          <h2 className="text-xl font-bold text-gray-800">{d.titulo}</h2>

          <div>
            <h3 className="text-sm font-semibold text-gray-600 mb-1">Contexto</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{d.contexto}</p>
          </div>

          {d.opcoes.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 mb-2">Opcoes Avaliadas</h3>
              <div className="space-y-3">
                {d.opcoes.map((o, i) => (
                  <div key={i} className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-800 mb-2">{o.nome}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-green-700 mb-1">Pros</p>
                        <ul className="text-sm text-gray-600 space-y-0.5">
                          {o.pros.map((p, j) => <li key={j} className="flex gap-1"><span className="text-green-500">+</span>{p}</li>)}
                        </ul>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-red-700 mb-1">Contras</p>
                        <ul className="text-sm text-gray-600 space-y-0.5">
                          {o.contras.map((c, j) => <li key={j} className="flex gap-1"><span className="text-red-500">-</span>{c}</li>)}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {d.decisao && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 mb-1">Decisao</h3>
              <p className="text-sm text-gray-700 bg-green-50 p-3 rounded-lg">{d.decisao}</p>
            </div>
          )}
          {d.justificativa && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 mb-1">Justificativa</h3>
              <p className="text-sm text-gray-700">{d.justificativa}</p>
            </div>
          )}
          {d.consequencias && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 mb-1">Consequencias</h3>
              <p className="text-sm text-gray-700">{d.consequencias}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <div className="flex gap-2">
            {d.status === 'proposta' && (
              <>
                <button onClick={() => onStatusChange('aceita')} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700">Aceitar</button>
                <button onClick={() => onStatusChange('rejeitada')} className="text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700">Rejeitar</button>
                <button onClick={() => onStatusChange('adiada')} className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700">Adiar</button>
              </>
            )}
          </div>
          <button onClick={onEdit} className="text-sm px-4 py-2 border rounded-lg hover:bg-gray-100">Editar</button>
        </div>
      </div>
    </div>
  )
}

function DecisaoForm({ decisao, onClose, onSaved }: {
  decisao: Decisao | null
  onClose: () => void
  onSaved: () => void
}) {
  const [titulo, setTitulo] = useState(decisao?.titulo ?? '')
  const [contexto, setContexto] = useState(decisao?.contexto ?? '')
  const [dec, setDec] = useState(decisao?.decisao ?? '')
  const [justificativa, setJustificativa] = useState(decisao?.justificativa ?? '')
  const [consequencias, setConsequencias] = useState(decisao?.consequencias ?? '')
  const [opcoes, setOpcoes] = useState(decisao?.opcoes ?? [])
  const [saving, setSaving] = useState(false)

  const addOpcao = () => setOpcoes([...opcoes, { nome: '', pros: [''], contras: [''] }])

  const handleSave = async () => {
    if (!titulo.trim() || !contexto.trim()) return
    setSaving(true)
    try {
      const data = {
        titulo: titulo.trim(),
        contexto: contexto.trim(),
        opcoes: opcoes.filter((o) => o.nome.trim()),
        decisao: dec.trim() || undefined,
        justificativa: justificativa.trim() || undefined,
        consequencias: consequencias.trim() || undefined,
      }
      if (decisao) {
        await api.updateDecisao(decisao.id, data)
      } else {
        await api.createDecisao(data)
      }
      onSaved()
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-8 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 mb-12" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">{decisao ? 'Editar Decisao' : 'Nova Decisao'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Titulo da decisao..."
            className="w-full text-lg font-medium border-b pb-2 focus:outline-none focus:border-blue-500" />
          <div>
            <label className="text-xs text-gray-500 block mb-1">Contexto (por que essa decisao e necessaria?)</label>
            <textarea value={contexto} onChange={(e) => setContexto(e.target.value)} rows={3}
              className="w-full text-sm border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          {/* Opcoes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-gray-500">Opcoes</label>
              <button onClick={addOpcao} className="text-xs text-blue-600 hover:underline">+ Opcao</button>
            </div>
            {opcoes.map((o, i) => (
              <div key={i} className="border rounded-lg p-3 mb-2">
                <input value={o.nome} onChange={(e) => { const n = [...opcoes]; n[i] = { ...n[i], nome: e.target.value }; setOpcoes(n) }}
                  placeholder="Nome da opcao..." className="w-full text-sm font-medium border-b pb-1 mb-2 focus:outline-none" />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] text-green-700 font-semibold mb-1">Pros</p>
                    {o.pros.map((p, j) => (
                      <input key={j} value={p} onChange={(e) => {
                        const n = [...opcoes]; n[i] = { ...n[i], pros: n[i].pros.map((x, k) => k === j ? e.target.value : x) }; setOpcoes(n)
                      }} placeholder="Pro..." className="w-full text-xs border-b mb-1 pb-0.5 focus:outline-none" />
                    ))}
                    <button onClick={() => { const n = [...opcoes]; n[i] = { ...n[i], pros: [...n[i].pros, ''] }; setOpcoes(n) }}
                      className="text-[10px] text-green-600 hover:underline">+ pro</button>
                  </div>
                  <div>
                    <p className="text-[10px] text-red-700 font-semibold mb-1">Contras</p>
                    {o.contras.map((c, j) => (
                      <input key={j} value={c} onChange={(e) => {
                        const n = [...opcoes]; n[i] = { ...n[i], contras: n[i].contras.map((x, k) => k === j ? e.target.value : x) }; setOpcoes(n)
                      }} placeholder="Contra..." className="w-full text-xs border-b mb-1 pb-0.5 focus:outline-none" />
                    ))}
                    <button onClick={() => { const n = [...opcoes]; n[i] = { ...n[i], contras: [...n[i].contras, ''] }; setOpcoes(n) }}
                      className="text-[10px] text-red-600 hover:underline">+ contra</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="text-xs text-gray-500 block mb-1">Decisao tomada (opcional)</label>
            <textarea value={dec} onChange={(e) => setDec(e.target.value)} rows={2}
              className="w-full text-sm border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Justificativa (opcional)</label>
            <textarea value={justificativa} onChange={(e) => setJustificativa(e.target.value)} rows={2}
              className="w-full text-sm border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Consequencias (opcional)</label>
            <textarea value={consequencias} onChange={(e) => setConsequencias(e.target.value)} rows={2}
              className="w-full text-sm border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="text-sm px-4 py-2 border rounded-lg hover:bg-gray-100">Cancelar</button>
          <button onClick={handleSave} disabled={saving || !titulo.trim() || !contexto.trim()}
            className="text-sm px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Salvando...' : decisao ? 'Salvar' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  )
}
